import { inspectMessage } from "@chatbotgang/etude/debug/inspectMessage";
import { ErrorSchema } from "@polifonia/interlude/models";
import { fakeT } from "@polifonia/utils/react/fakeT";
import { AxiosError, CanceledError, HttpStatusCode } from "axios";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import type { ConditionalKeys, Tagged } from "type-fest";

import { ResourceNotFoundError } from "@/features/error";
import { mutationErrorEventEmitter } from "@/features/error/mutationErrorEventEmitter";
import { logError } from "@/features/logger";
import type { InterludeTypes } from "@/interlude";
import { UnAuthError } from "@/interlude/interludeAxios";

const t = fakeT;

type NetworkErrorSymbol = Tagged<symbol, "NetworkError">;

const networkError = {
  message: "",
  code: Symbol("NetworkError") as NetworkErrorSymbol,
};

type NoTokenErrorSymbol = Tagged<symbol, "NoTokenError">;

const noTokenError = {
  message: "",
  code: Symbol("NoTokenError") as NoTokenErrorSymbol,
};

type ResourceNotFoundErrorSymbol = Tagged<symbol, "ResourceNotFoundError">;

const resourceNotFoundError = {
  message: "",
  code: Symbol("ResourceNotFoundError") as ResourceNotFoundErrorSymbol,
};

type ApiErrorResponseData =
  | InterludeTypes["Error"]
  | typeof networkError
  | typeof noTokenError
  | typeof resourceNotFoundError;

type ApiErrorCode = ApiErrorResponseData["code"];

const apiErrorCodeTranslationKeyMap = {
  [networkError.code]: t("common.apiError.networkError"),
  [noTokenError.code]: t("errorPage.tryFromTheOriginalApp"),
  [resourceNotFoundError.code]: t("common.apiError.resourceNotFound"),
  UNAUTHORIZED: t("errorPage.tryFromTheOriginalApp"),
  CHANNEL_WCCS_INFORMATION_URL_ALREADY_EXISTS: null,
  ORGANIZATION_NOT_FOUND: null,
  BALANCE_INSUFFICIENT: null,
  SUBSCRIPTION_NOT_FOUND: null,
  MONTHLY_USAGE_REPORT_NOT_FOUND: null,
  UNIFY_SCOPE_SETTING_NOT_FOUND: null,
  INTEGRATE_CDH_FAILED: null,
  UNIFY_KEY_UPDATE_FAIL: null,
  DISCONNECT_CDH_FAILED: null,
  GET_MERGE_STATE_FAILED: null,
  SERVE_CONTACT_UPDATE_EVENT_FAILED: null,
  GET_ORGANIZATIONS_FAILED: null,
  CHANNEL_NOT_FOUND: t("common.apiError.resourceNotFound"),
  CHANNEL_WCCS_INFORMATION_NOT_FOUND: null,
  ORGANIZATION_SETTING_NOT_FOUND: null,
  // WebStream subdomain errors
  CHANNEL_SUBDOMAIN_INVALID: null,
  CHANNEL_SUBDOMAIN_ALREADY_EXISTS: null,
  CHANNEL_ALREADY_EXISTS: null,
  IG_BUSINESS_ACCOUNT_NOT_FOUND: null,
  UNKNOWN_ERROR: t("common.applicationError.unknownError"),
  GOOGLE_ADS_INVALID_MCC_ID: t(
    "applications.googleAds.registerManagerAccount.error.invalidMccId",
  ),
  GOOGLE_ADS_ACCESS_NOT_GRANTED: t(
    "applications.googleAds.registerManagerAccount.error.accessNotGranted",
  ),
  GOOGLE_ADS_CANNOT_DELETE_MANAGER_ACCOUNT: t(
    "applications.googleAds.deleteAccountModal.cannotDeleteManagerAccount",
  ),
  SENDER_DOMAIN_NOT_FOUND: t("common.apiError.resourceNotFound"),
  SENDER_PROFILE_NOT_FOUND: t("common.apiError.resourceNotFound"),
  // Handled locally by the sender-profile form so the message can point at
  // the offending field instead of surfacing as a generic global snackbar.
  SENDER_PROFILE_ALREADY_EXISTS: null,
  SENDER_PROFILE_DOMAIN_MISMATCH: null,
  INVALID_REQUEST: t("common.apiError.invalidRequest"),
  EXTERNAL_SERVICE_ERROR: t("common.apiError.externalServiceError"),
  // Custom Field errors
  CUSTOM_FIELD_NOT_FOUND: t("common.apiError.resourceNotFound"),
  CUSTOM_FIELD_KEY_DUPLICATE: null,
  CUSTOM_FIELD_NAME_DUPLICATE: null,
  CUSTOM_FIELD_ACTIVE_LIMIT_EXCEEDED: null,
  CUSTOM_FIELD_SLOT_LIMIT_EXCEEDED: null,
  // Field-level, not single-select-specific: the PATCH body carried none of
  // the three keys it can change. Grouped with the other field codes here and
  // in `packages/interlude/models.ts`.
  CUSTOM_FIELD_PATCH_EMPTY: t("common.apiError.invalidRequest"),
  // Single select custom field errors.
  //
  // All of them point at the same already-published string for now. Pointing
  // one at an unpublished key would show the key itself to the user and report
  // a missing translation to Sentry, and nothing reaches these paths before the
  // option editor exists.
  //
  // Every one of them gets wording of its own once the keys are published; the
  // contract defines all twelve, so each has something specific to say. Two of
  // them, OPTIONS_STALE and OPTIONS_DUPLICATE, also become `null` in the pull
  // request that builds the option editor, so their message can sit next to the
  // offending row; the rest keep the global snackbar and change only the string.
  //
  // The last four are additionally reported -- see `contractDriftErrorCodes`.
  CUSTOM_FIELD_SINGLE_SELECT_NOT_ENABLED: t("common.apiError.invalidRequest"),
  CUSTOM_FIELD_SINGLE_SELECT_FIELD_TYPE_INVALID: t(
    "common.apiError.invalidRequest",
  ),
  // Handled where the option editor is: each needs a message tied to the
  // state of that form, which a global snackbar cannot give.
  //   STALE -- the list changed under this edit and has been reseeded, so the
  //     user's unsaved changes are gone and they have to redo them.
  //   DUPLICATE -- one option name collides with another, archived ones
  //     included, and the fix is to rename it. A reseed is armed here too,
  //     because the collision is almost always with a name someone else just
  //     took: the editor disables submit while two of its own rows share a
  //     name, so a duplicate it did send came from the server's copy, not
  //     this form's. It only fires if the refetch brings back different data.
  CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_STALE: null,
  CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_DUPLICATE: null,
  CUSTOM_FIELD_SINGLE_SELECT_OPTIONS_LIMIT_EXCEEDED: t(
    "common.apiError.invalidRequest",
  ),
  CUSTOM_FIELD_SINGLE_SELECT_OPTION_NAME_INVALID: t(
    "common.apiError.invalidRequest",
  ),
  CUSTOM_FIELD_SINGLE_SELECT_OPTION_DESCRIPTION_INVALID: t(
    "common.apiError.invalidRequest",
  ),
  CUSTOM_FIELD_SINGLE_SELECT_OPTION_ACTIVE_INVALID: t(
    "common.apiError.invalidRequest",
  ),
  CUSTOM_FIELD_SINGLE_SELECT_OPTION_REQUIRED_FIELD_MISSING: t(
    "common.apiError.invalidRequest",
  ),
  CUSTOM_FIELD_SINGLE_SELECT_OPTION_READONLY_FIELD: t(
    "common.apiError.invalidRequest",
  ),
  CUSTOM_FIELD_SINGLE_SELECT_OPTION_UNKNOWN_FIELD: t(
    "common.apiError.invalidRequest",
  ),
  // Billing errors
  BILLING_NOT_CONFIGURED: null,
  // interlude could not get Arioso to sign the SMTM SSO token (502). The
  // navigation hook only closes the blank tab, so the message has to come
  // from here.
  ACCESS_TOKEN_ISSUE_FAILED: t("common.apiError.externalServiceError"),
  // Email Onboard errors
  EMAIL_ONBOARD_ORG_ALREADY_HAS_CHANNEL: null,
  EMAIL_ONBOARD_BRAND_DOMAIN_TAKEN: null,
  EMAIL_ONBOARD_APPLICATION_NOT_FOUND: null,
  // Meta Ads errors
  META_ADS_INVALID_TOKEN: null,
  META_ADS_ALREADY_CONNECTED: null,
  META_ADS_NOT_CONNECTED: null,
  // LINE Ads errors. The LINE Ads page shows them itself, with a link to the
  // Data Provider API request form that a snackbar cannot carry.
  LINE_ADS_INVALID_CREDENTIALS: null,
  LINE_ADS_DATA_PROVIDER_PERMISSION_REQUIRED: null,
  LINE_ADS_UNAVAILABLE: null,
  // Contract signing. Mapped here rather than shown by the signing page, so a
  // failure produces one message instead of a generic snackbar beside a
  // page-level box — and so it is not reported to Sentry as an unknown error.
  SIGNING_NOT_ALLOWED: t("contractSigning.error.notAllowed"),
  INVALID_AGREEMENT_VERSIONS: t("contractSigning.error.staleVersions"),
  CONTRACT_SIGNING_NOT_AVAILABLE: t("contractSigning.error.unavailable"),
  // OpenAPI v2 self-service API-key errors.
  //
  // All four are global rather than `null`: only the api-key page knows what
  // they mean, and until now none of them produced anything at all, because
  // they were missing from `ErrorSchema` and the parse failure sent them to
  // `logError`. Every string here has to read correctly from any call in the
  // app, not only from that page.
  //
  // Three of them reuse a published string rather than introducing a key of
  // their own, for the reason the single-select block above gives: an
  // unpublished key renders as the key itself and files a missing-translation
  // report. A 404 on a credential or an org is a resource that is not there,
  // and an unreachable Kong is the same shape as `ACCESS_TOKEN_ISSUE_FAILED`,
  // the other 502 from a service interlude calls on the caller's behalf.
  //
  // `OPENAPI_V2_NOT_ENABLED` is the one with nothing published to say it, so
  // it gets a new key -- which shows as the key until that key is published.
  // Unlike `ORGANIZATION_NOT_FOUND` two screens up, which is `null` because the
  // pages raising it answer for it themselves, this one can arrive from a
  // mutation on a page with nowhere to put it.
  OPENAPI_V2_NOT_ENABLED: t("common.apiError.openApiV2NotEnabled"),
  OPENAPI_ORG_NOT_FOUND: t("common.apiError.resourceNotFound"),
  OPENAPI_CREDENTIAL_NOT_FOUND: t("common.apiError.resourceNotFound"),
  OPENAPI_KONG_UNAVAILABLE: t("common.apiError.externalServiceError"),
} satisfies Record<ApiErrorCode, ReturnType<typeof t> | null>;

type NonGlobalApiError = ConditionalKeys<
  typeof apiErrorCodeTranslationKeyMap,
  null
>;

const nonGlobalApiErrors: Array<NonGlobalApiError> = Object.entries(
  apiErrorCodeTranslationKeyMap,
).flatMap(([key, value]) =>
  value === null ? [key] : [],
) as Array<NonGlobalApiError>;

function isNonGlobalApiError(code: ApiErrorCode): code is NonGlobalApiError {
  return nonGlobalApiErrors.includes(code as NonGlobalApiError);
}

/**
 * Codes this client cannot produce. `PatchCustomFieldOptionRequestSchema` is a
 * strict union that rejects all four shapes -- a non-boolean `active`, a new
 * option with no `name`, an option `key`, an unknown key such as `status` --
 * before the request is sent, so one of these coming back means this build's
 * schema has drifted from the server's contract.
 *
 * They are shown to the user like any other code, because the contract defines
 * them and a sentence beats "unknown error". What they also need is a report,
 * and that is not automatic: until these codes were added to `ErrorSchema` they
 * failed to parse and reached `logError` through the fallback in
 * `getGlobalErrorTranslationKey` and `handleError`. Recognizing a code takes
 * that path away -- the same trade the contract signing codes above make
 * deliberately -- so the drift has to be reported explicitly or nobody finds
 * out the contract moved.
 */
const contractDriftErrorCodes: ReadonlyArray<ApiErrorCode> = [
  "CUSTOM_FIELD_SINGLE_SELECT_OPTION_ACTIVE_INVALID",
  "CUSTOM_FIELD_SINGLE_SELECT_OPTION_REQUIRED_FIELD_MISSING",
  "CUSTOM_FIELD_SINGLE_SELECT_OPTION_READONLY_FIELD",
  "CUSTOM_FIELD_SINGLE_SELECT_OPTION_UNKNOWN_FIELD",
];

function reportContractDrift(error: unknown, code: ApiErrorCode): void {
  if (!contractDriftErrorCodes.includes(code)) return;
  logError(error);
}

/**
 * Get the error body from an unknown error. Return null if the error is not
 * valid.
 */
function getApiErrorBodyFromUnknownError(
  input: unknown,
): ApiErrorResponseData | null {
  /**
   * Check if the error is a specific type of error
   */
  if (input instanceof UnAuthError) {
    return noTokenError;
  }
  if (input instanceof ResourceNotFoundError) {
    return resourceNotFoundError;
  }

  if (input instanceof AxiosError) {
    if (input.code === "ERR_NETWORK") return networkError;

    // Try to parse error code from response body first
    const parseErrorResult = ErrorSchema.safeParse(input.response?.data);
    if (parseErrorResult.success) return parseErrorResult.data;

    // Fallback to HTTP status code if no error code in response body
    if (input.response?.status === HttpStatusCode.NotFound)
      return resourceNotFoundError;
  }
  return null;
}

/**
 * Handle non-global errors. Return `true` if the error is handled.
 */
async function handleNonGlobalApiError(
  err: unknown,
  handler: {
    [key in NonGlobalApiError]?: (
      error: InterludeTypes["Error"] & {
        name: key;
      },
    ) => void | Promise<void>;
  },
): Promise<boolean> {
  const errorApiBody = getApiErrorBodyFromUnknownError(err);
  if (!errorApiBody) return false;
  if (!isNonGlobalApiError(errorApiBody.code)) return false;
  const callback = handler[errorApiBody.code];
  if (!callback) return false;
  // @ts-expect-error -- TS2590: Expression produces a union type that is too complex to represent.
  await callback(errorApiBody);
  return true;
}

export const getGlobalErrorTranslationKey = (error: unknown) => {
  const parsedError = getApiErrorBodyFromUnknownError(error);
  if (parsedError) {
    reportContractDrift(error, parsedError.code);
    return apiErrorCodeTranslationKeyMap[parsedError.code];
  }
  logError(error);
};

/**
 * Hook to get error message from any error including cancellation error and
 * non-global error.
 */
function handleError<T = Error>(
  /**
   * The error to handle.
   */
  error: T,
): ReturnType<typeof t> | null {
  /**
   * Cancel error is not an error, it's just a signal to cancel the request.
   */
  if (error instanceof CanceledError) return null;
  const errorApiBody = getApiErrorBodyFromUnknownError(error);
  if (errorApiBody) {
    reportContractDrift(error, errorApiBody.code);
    mutationErrorEventEmitter.emit("knownError", errorApiBody);
    // fixme: ?? null
    return apiErrorCodeTranslationKeyMap[errorApiBody.code] ?? null;
  }
  logError(error);
  return t("common.applicationError.unknownError");
}

type UseGetErrorMessageOptions = {
  unknownErrorMessage?: string;
};

/**
 * Hook to get error message from any error including cancellation error and
 * non-global error.
 */
function useGetErrorMessage() {
  const { t } = useTranslation();
  const getErrorMessage = useCallback<
    (e: unknown, options?: UseGetErrorMessageOptions) => string
  >(
    function getErrorMessage(e, options) {
      const errorApiBody = getApiErrorBodyFromUnknownError(e);
      const translationKey =
        (errorApiBody && errorApiBody?.code !== "UNKNOWN_ERROR"
          ? apiErrorCodeTranslationKeyMap[errorApiBody.code]
          : apiErrorCodeTranslationKeyMap.UNKNOWN_ERROR) ??
        apiErrorCodeTranslationKeyMap.UNKNOWN_ERROR;

      return (
        (translationKey === apiErrorCodeTranslationKeyMap.UNKNOWN_ERROR
          ? options?.unknownErrorMessage ||
            t(apiErrorCodeTranslationKeyMap.UNKNOWN_ERROR)
          : t(translationKey)) +
        (translationKey !== apiErrorCodeTranslationKeyMap.UNKNOWN_ERROR
          ? ""
          : `: ${
              e instanceof Error
                ? `[${e.name}]: ${e.message}`
                : inspectMessage`Non-error object thrown: ${e}`
            }`)
      );
    },
    [t],
  );
  /**
   * Get error message from any error including cancellation error and
   * non-global error.
   */
  return getErrorMessage;
}

export {
  apiErrorCodeTranslationKeyMap as apiErrorNameTransUtilsMap,
  getApiErrorBodyFromUnknownError,
  handleError,
  handleNonGlobalApiError,
  isNonGlobalApiError,
  useGetErrorMessage,
};
export type { ApiErrorCode, ApiErrorResponseData, NonGlobalApiError };
